import type { Entity, EventHandler, AppBase } from 'playcanvas';
import { Color } from 'playcanvas';
import type { Collision } from '../../submodules/supersplat-viewer/src/collision';
import type { Global } from '../../submodules/supersplat-viewer/src/types';
import type { App } from '../../submodules/supersplat-viewer/src/app';
import type { ViewerAnnotation } from '@mindcloudxai/shared';
import type { PcVec3 } from './constants';

export interface SplatHandle {
  readonly entity: Entity;
  dispose(): void;
}

export interface WalkControllerStateSnapshot {
  eyePosition: PcVec3;
  footPosition: PcVec3;
  velocity: PcVec3;
  grounded: boolean;
  jumping: boolean;
  jumpStarted: boolean;
  jumpHadInput: boolean;
  yaw: number;
  moveMagnitude: number;
}

export interface WalkControllerRuntime {
  navPosition: PcVec3;
  getAvatarState?: (result: WalkControllerStateSnapshot) => WalkControllerStateSnapshot;
}

/** supersplat 内部 Viewer 运行时实例的最小访问面（避免 any 扩散）。 */
export interface SuperSplatRuntime {
  picker?: {
    pick: (x: number, y: number) => Promise<PcVec3 | null>;
  };
  cameraManager?: {
    camera: {
      look: (position: PcVec3, target: PcVec3, up?: PcVec3) => void;
      calcFocusPoint: (out: PcVec3) => void;
      fov: number;
    };
    snap: () => void;
    walkController?: WalkControllerRuntime;
    avatarController?: WalkControllerRuntime;
  };
  inputController?: {
    collision?: Collision | null;
    frame: {
      deltas: {
        move: {
          append: (value: [number, number, number]) => void;
        };
      };
    };
  };
  measureTool?: {
    activate: () => void;
    deactivate: () => void;
    toggle: () => boolean;
    clear: () => void;
    isActive: () => boolean;
    setUnitSystem: (system: 'metric' | 'imperial') => void;
  };
  destroy?: () => void;
}

export interface SuperSplatViewerConstructor {
  new (
    global: Global,
    gsplatLoad: Promise<Entity>,
    skyboxLoad?: Promise<void>,
    collisionLoad?: Promise<Collision>,
  ): SuperSplatRuntime;
}

export interface DebugLineApp extends AppBase {
  drawLine(start: PcVec3, end: PcVec3, color: Color, depthTest?: boolean): void;
}

interface ImportMetaWithGlob {
  glob(pattern: string | string[]): Record<string, () => Promise<unknown>>;
}

const superSplatViewerModules = (import.meta as unknown as ImportMetaWithGlob).glob(
  '../../submodules/supersplat-viewer/src/viewer.ts',
) satisfies ReturnType<ImportMetaWithGlob['glob']>;

export async function loadSuperSplatViewerModule(): Promise<SuperSplatViewerConstructor> {
  const load = superSplatViewerModules['../../submodules/supersplat-viewer/src/viewer.ts'];
  if (!load) {
    throw new Error('SuperSplatViewer: runtime module not found');
  }
  const mod = (await load()) as { Viewer: SuperSplatViewerConstructor };
  return mod.Viewer;
}

/** SuperSplatViewer 内部状态快照，供各 sub-system API 消费。 */
export interface ViewerContext {
  readonly global: Global | undefined;
  readonly viewer?: SuperSplatRuntime;
  readonly canvas: HTMLCanvasElement;
  readonly camera: Entity;
  readonly app: App;
  readonly annotations: ViewerAnnotation[];
  readonly events: EventHandler;
  readonly disposed: boolean;
  readonly initialized: boolean;
}
